/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState, useEffect, useCallback } from 'react';
import { PortalSettings, apiRequest } from './constants';
import { usePortalActions } from './usePortalActions';
import { normalizePortalSettings } from './normalizePortalSettings';

/**
 * Fetches portal settings for an agency.
 *
 * A failed read is null, never "nothing connected": empty defaults made the
 * page offer to connect portals that were connected, and the next save wrote
 * that empty state back (switching OLX and the other portals off).
 *
 * @param agencyId - The identifier of the agency.
 * @returns The normalized portal settings, or null when the read failed.
 */
export async function fetchPortalSettings(agencyId: string): Promise<PortalSettings | null> {
  try {
    const raw = await apiRequest(`/portal-integration/agencies/${agencyId}/settings`, 'GET');
    return normalizePortalSettings(raw, agencyId);
  } catch {
    return null;
  }
}

/**
 * Manages portal settings state, loading, and mutation actions for an agency.
 *
 * @param agencyId - The identifier of the agency, or undefined.
 * @param t - The translation function.
 * @returns The settings, loading/saving/load-failed flags, and portal mutation actions.
 */
export function usePortalSettings(agencyId: string | undefined, t: (key: string, options?: any) => string) {
  const [settings, setSettings] = useState<PortalSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  const fetchSettings = useCallback(async () => {
    if (!agencyId) return;
    setLoading(true);
    const result = await fetchPortalSettings(agencyId);
    // A failed refresh keeps what was last read from the server.
    if (result) setSettings(result);
    setLoadFailed(!result);
    setLoading(false);
  }, [agencyId]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const actions = usePortalActions({ agencyId, t, setSettings, setSaving, fetchSettings });

  return { settings, loading, saving, loadFailed, fetchSettings, ...actions };
}
